import InfoIcon from '@mui/icons-material/Info';
import { Grid, Tooltip, Typography } from '@mui/material';
import { includes } from 'lodash';
import { BooleanField, FunctionField, Labeled, SimpleShowLayout, TextField, useRecordContext } from 'react-admin';
import { RetirementAccountTypeDto } from 'src/dtos';
import { AccountStatusChip } from 'src/lib';
import { Account } from 'src/models';
import { Spacing } from 'src/styles';

import { SyncAccountListStatusButton } from '../SyncAccountListStatusButton';

import { ResetFailedAccountAttemptsButton } from './ResetFailedAccountCreateAttemptsButton';

export const AccountDetails = () => {
  const record = useRecordContext<Account>();
  const { id } = record;

  if (!record) {
    return null;
  }

  return (
    <Grid container direction='row'>
      <Grid item md={12}>
        <SimpleShowLayout>
          <Typography variant='h5'>Account Information</Typography>
        </SimpleShowLayout>
      </Grid>
      <Grid item md={6}>
        <SimpleShowLayout>
          <Labeled>
            <TextField source='accountId' label='Account Id' />
          </Labeled>
        </SimpleShowLayout>
        <SimpleShowLayout>
          <Labeled>
            <TextField source='accountNumber' label='Account Number' />
          </Labeled>
        </SimpleShowLayout>
      </Grid>
      <Grid item md={6}>
        <SimpleShowLayout>
          <Labeled>
            <TextField source='type.label' label='Account Type' />
          </Labeled>
        </SimpleShowLayout>
        {record.type.isJoint && (
          <SimpleShowLayout>
            <Labeled>
              <TextField source='jointAccountType.label' label='Joint Account Type' />
            </Labeled>
          </SimpleShowLayout>
        )}
        {record.type.isRetirement && (
          <SimpleShowLayout>
            <Labeled>
              <TextField source='retirementAccountType.label' label='Retirement Account Type' />
            </Labeled>
          </SimpleShowLayout>
        )}
        {record.type.isEntity && (
          <SimpleShowLayout>
            <Labeled>
              <TextField source='entityAccountType.label' label='Entity Account Type' />
            </Labeled>
          </SimpleShowLayout>
        )}
      </Grid>
      {record.type.isJoint && record.jointAccountType?.isCommunity && (
        <Grid item md={6}>
          <SimpleShowLayout>
            <Labeled>
              <TextField source='jointCommunityPropertyState.label' label='Community Property State' />
            </Labeled>
          </SimpleShowLayout>
        </Grid>
      )}
      {record.type.isRetirement && record.retirementAccountType?.isSimple && (
        <Grid item md={6}>
          <SimpleShowLayout>
            <Labeled>
              <TextField source='simpleIraPlanName' label='Simple IRA Plan Name' />
            </Labeled>
          </SimpleShowLayout>
        </Grid>
      )}
      {record.type.isRetirement &&
        includes(
          [RetirementAccountTypeDto.Beneficiary, RetirementAccountTypeDto.RothBeneficiary],
          record.retirementAccountType?.value,
        ) && (
          <Grid item md={6}>
            <SimpleShowLayout>
              <Labeled>
                <TextField source='beneficiaryIraDecedentName' label='Beneficiary Ira Decedent Name' />
              </Labeled>
            </SimpleShowLayout>
          </Grid>
        )}
      <Grid item md={6}>
        <SimpleShowLayout>
          <Labeled>
            <BooleanField source='isAccredited' label='Is Accredited' />
          </Labeled>
        </SimpleShowLayout>
      </Grid>
      <Grid item md={6}>
        <SimpleShowLayout>
          <Labeled>
            <FunctionField
              label='Account Status (My IPO Server)'
              render={(account: Account) => {
                return (
                  <>
                    {account?.status && <AccountStatusChip value={account.status} />}
                    {Boolean(account?.accountId) && <SyncAccountListStatusButton ids={[id]} />}
                  </>
                );
              }}
            />
          </Labeled>
        </SimpleShowLayout>
      </Grid>
      <Grid item md={12} className={Spacing.my32}>
        <Grid container direction='row'>
          <Grid item md={12}>
            <SimpleShowLayout>
              <Typography variant='h6'>Account Create Errors</Typography>
            </SimpleShowLayout>
          </Grid>
          <Grid item md={6}>
            <SimpleShowLayout>
              <Labeled>
                <TextField label='Last Failed Account Create Error' source='lastFailedCreateAttemptError' />
              </Labeled>
            </SimpleShowLayout>
          </Grid>
          <Grid item md={6}>
            <SimpleShowLayout>
              <Labeled>
                <FunctionField
                  label='Failed Account Create Attempts'
                  render={() => {
                    return (
                      <Grid container direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
                        <Grid item className={Spacing.mt8}>
                          <Grid container direction='row' alignItems='center'>
                            <Typography variant='body1' className={Spacing.mr4}>
                              {record.failedCreateAttempts}
                            </Typography>
                            <Tooltip title='Maximum number of create attempts is 3.'>
                              <InfoIcon fontSize='small' color='info' />
                            </Tooltip>
                          </Grid>
                        </Grid>
                        <Grid item>
                          <ResetFailedAccountAttemptsButton id={id} failedCreateAttempts />
                        </Grid>
                      </Grid>
                    );
                  }}
                />
              </Labeled>
            </SimpleShowLayout>
          </Grid>
        </Grid>
      </Grid>
      <Grid item md={12} className={Spacing.my32}>
        <Grid container direction='row'>
          <Grid item md={12}>
            <SimpleShowLayout>
              <Typography variant='h6'>Account Link Errors</Typography>
            </SimpleShowLayout>
          </Grid>
          <Grid item md={6}>
            <SimpleShowLayout>
              <Labeled>
                <TextField label='Last Failed Account Link Error' source='lastFailedLinkAttemptError' />
              </Labeled>
            </SimpleShowLayout>
          </Grid>
          <Grid item md={6}>
            <SimpleShowLayout>
              <Labeled>
                <FunctionField
                  label='Failed Account Link Attempts'
                  render={() => {
                    return (
                      <Grid container direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
                        <Grid item className={Spacing.mt8}>
                          <Grid container direction='row' alignItems='center'>
                            <Typography variant='body1' className={Spacing.mr4}>
                              {record.failedLinkAttempts}
                            </Typography>
                            <Tooltip title='Maximum number of link attempts is 3.'>
                              <InfoIcon fontSize='small' color='info' />
                            </Tooltip>
                          </Grid>
                        </Grid>
                        <Grid item>
                          <ResetFailedAccountAttemptsButton id={id} failedLinkAttempts />
                        </Grid>
                      </Grid>
                    );
                  }}
                />
              </Labeled>
            </SimpleShowLayout>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
